// Serialized draft-lifecycle state machine (#1594). Replaces the prior
// ~5 interacting booleans (`draftSaving`, `draftSending`, local
// `closing`/`deleting`, and their scattered guard expressions) with ONE
// explicit phase so save/send/close/delete can no longer race.
//
// @aiPurpose Single source of truth for which mutually-exclusive draft
//   lifecycle action is in flight, plus the pure guard predicates that
//   every call site (store transition actions, DraftDrawer guards,
//   DraftEditor onSend, useDraftSave) derives its busy/allowed state
//   from.
// @aiInvariant The machine is SERIALIZED: a lifecycle action may begin
//   ONLY from `'idle'`. Attempting to begin any action while not idle is
//   rejected (a no-op) — this is what kills the double-submit /
//   save-during-close races. Every begun action returns to `'idle'` via
//   a `'settle'` event (success OR failure) so a failed save/send/delete
//   never leaves a stuck spinner.
// @aiInvariant `pendingPhotoUploads` is NOT part of this phase. Uploads
//   are genuinely concurrent with idle editing, so they stay a separate
//   counter in the store. Close/delete are gated on BOTH
//   `activity === 'idle'` AND `pendingPhotoUploads === 0` via
//   `canClose` / `canBeginDelete`.

/**
 * The mutually-exclusive lifecycle action currently in flight for the
 * draft scratchpad. `'idle'` means no save/send/close/delete is running
 * (ordinary editing + concurrent photo uploads happen in this phase).
 */
export type DraftActivity = 'idle' | 'saving' | 'sending' | 'closing' | 'deleting';

/**
 * Events that drive the machine. The four `begin-*` events start a
 * lifecycle action (allowed only from `'idle'`); `'settle'` returns to
 * `'idle'` after the action resolves (success or failure).
 */
export type BeginActivityEvent = 'begin-save' | 'begin-send' | 'begin-close' | 'begin-delete';
export type DraftActivityEvent = BeginActivityEvent | 'settle';

/** True while any lifecycle action is in flight (i.e. not `'idle'`). */
export function isBusy(activity: DraftActivity): boolean {
  return activity !== 'idle';
}

/** A save may begin only from `'idle'`. */
export function canBeginSave(activity: DraftActivity): boolean {
  return activity === 'idle';
}

/** A send may begin only from `'idle'`. */
export function canBeginSend(activity: DraftActivity): boolean {
  return activity === 'idle';
}

/**
 * A close may begin only from `'idle'` AND with no photo upload in
 * flight. The upload gate prevents an upload-resolved key from landing
 * in `unsavedPhotoKeys` after `closeDraft` already wiped the scratchpad
 * (orphan storage object).
 */
export function canClose(activity: DraftActivity, pendingPhotoUploads: number): boolean {
  return activity === 'idle' && pendingPhotoUploads === 0;
}

/**
 * A destructive delete may begin only from `'idle'` AND with no photo
 * upload in flight (same orphan-storage reason as `canClose`).
 */
export function canBeginDelete(activity: DraftActivity, pendingPhotoUploads: number): boolean {
  return activity === 'idle' && pendingPhotoUploads === 0;
}

const BEGIN_TARGET: Record<BeginActivityEvent, DraftActivity> = {
  'begin-save': 'saving',
  'begin-send': 'sending',
  'begin-close': 'closing',
  'begin-delete': 'deleting',
};

/**
 * Pure transition reducer. A `begin-*` event transitions only when the
 * machine is `'idle'`; otherwise the current phase is returned unchanged
 * (the begin is rejected). `'settle'` always returns to `'idle'`.
 *
 * @aiInvariant Begin-from-non-idle is a no-op (returns `current`). The
 *   caller MUST detect rejection by comparing the result to `current`
 *   (or by gating on `canBegin*` first) before issuing any network I/O.
 */
export function nextActivity(current: DraftActivity, event: DraftActivityEvent): DraftActivity {
  if (event === 'settle') return 'idle';
  if (current !== 'idle') return current;
  return BEGIN_TARGET[event];
}

/**
 * Minimal structural type for the holder of the `draftActivity` phase
 * (the Zustand store satisfies it). Kept here so the begin helper can be
 * unit-tested without the React store.
 */
export interface ActivityHolder {
  draftActivity: DraftActivity;
}

/**
 * Apply a `begin-*` event through `set` and report whether the machine
 * was claimed. Runs the reducer INSIDE the updater so two synchronous
 * begins in one tick cannot both observe a stale `'idle'`. A rejected
 * begin returns `false` and mutates nothing.
 *
 * The optional `guard` is evaluated against the LIVE state inside the
 * same `set` updater, so it gates the claim on store fields beyond the
 * phase — used to reject close/delete atomically when
 * `pendingPhotoUploads !== 0`. Without this, a stale render closure
 * could pass a UI-level upload check yet still claim against a live
 * store with an upload in flight, orphaning a storage object after
 * `closeDraft` wipes the scratchpad.
 *
 * @aiInvariant Caller MUST issue network I/O only when this returns
 *   `true`; that is what guarantees exactly one round-trip per
 *   double-click. When `guard` is supplied it is checked against the
 *   live state in the same atomic `set` as the phase transition, so the
 *   claim itself enforces the guarded precondition.
 */
export function beginActivity<S extends ActivityHolder>(
  set: (updater: (state: S) => Partial<S> | S) => void,
  event: BeginActivityEvent,
  guard?: (state: S) => boolean,
): boolean {
  let claimed = false;
  set((s) => {
    const next = nextActivity(s.draftActivity, event);
    const passes = guard ? guard(s) : true;
    claimed = next !== s.draftActivity && passes;
    return claimed ? ({ draftActivity: next } as Partial<S>) : s;
  });
  return claimed;
}

/** Return the machine to `'idle'` through `set` (the `'settle'` event). */
export function settleActivity<S extends ActivityHolder>(
  set: (updater: (state: S) => Partial<S> | S) => void,
): void {
  set((s) => ({ draftActivity: nextActivity(s.draftActivity, 'settle') }) as Partial<S>);
}

/**
 * Live-state guard for a begin event: `begin-close` / `begin-delete` may
 * claim only when no photo upload is in flight (the orphan-storage gate,
 * #1594 AC-2); `begin-save` / `begin-send` are unguarded. Returns
 * `undefined` (no extra guard) for the unguarded events.
 */
export function uploadGuardFor<S extends ActivityHolder & { pendingPhotoUploads: number }>(
  event: BeginActivityEvent,
): ((state: S) => boolean) | undefined {
  return event === 'begin-close' || event === 'begin-delete'
    ? (s) => s.pendingPhotoUploads === 0
    : undefined;
}
